/**
 * Asset configuration for Poker Reborn
 *
 * Based on legacy AS3 client asset structure.
 * See LEGACY_DATA_USAGE.md for source tracking.
 */

// =============================================================================
// ASSET PATHS
// =============================================================================

/** Base asset paths (relative — required for Capacitor's file:// context) */
export const ASSET_PATHS = {
  /** Root assets directory */
  root: 'assets',

  /** Images */
  images: 'assets/images',

  /** Sprite atlases */
  atlases: 'assets/atlases',

  /** Sounds */
  sounds: 'sounds',

  /** Fonts */
  fonts: 'assets/fonts',
};

// =============================================================================
// TEXTURE ATLASES
// =============================================================================

/** Sprite atlas manifest */
export const ATLASES = {
  /** Card sprites (resolution variants — one loaded per session based on devicePixelRatio) */
  cards: {
    path1x: `${ASSET_PATHS.atlases}/cards_1x.json`,
    path2x: `${ASSET_PATHS.atlases}/cards_2x.json`,
    name: 'cards',
  },

  /** Chip sprites */
  chips: {
    path: `${ASSET_PATHS.atlases}/chips.json`,
    name: 'chips',
  },

  /** UI elements */
  ui: {
    path: `${ASSET_PATHS.atlases}/ui.json`,
    name: 'ui',
  },

  /** Table elements */
  table: {
    path: `${ASSET_PATHS.atlases}/table.json`,
    name: 'table',
  },
};

// =============================================================================
// INDIVIDUAL IMAGES
// =============================================================================

/** Individual image assets */
export const IMAGES = {
  /** Table background */
  tableBackground: `${ASSET_PATHS.images}/table/table_bg.webp`,

  /** Table felt */
  tableFelt: `${ASSET_PATHS.images}/table/felt.webp`,

  /** Dealer button */
  dealerButton: `${ASSET_PATHS.images}/table/dealer_button.png`,

  /** Card back (2x — retina/HiDPI) */
  cardBack: `${ASSET_PATHS.images}/cards/back.png`,

  /** Card back (1x — standard displays) */
  cardBack1x: `${ASSET_PATHS.images}/cards/back_1x.png`,

  /** Default avatar */
  defaultAvatar: `${ASSET_PATHS.images}/avatars/default.png`,

  /** Logo */
  logo: `${ASSET_PATHS.images}/logo.png`,
};

// =============================================================================
// CARD SPRITES
// Card naming convention: {rank}{suit}
// Ranks: 2-9, T, J, Q, K, A
// Suits: c (clubs), d (diamonds), h (hearts), s (spades)
// =============================================================================

/** Generate card sprite name */
export function getCardSpriteName(rank: string, suit: string): string {
  return `card_${rank}${suit}`;
}

/** All card sprite names */
export const CARD_SPRITES = (() => {
  const ranks = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'];
  const suits = ['c', 'd', 'h', 's'];
  const cards: string[] = [];

  for (const rank of ranks) {
    for (const suit of suits) {
      cards.push(getCardSpriteName(rank, suit));
    }
  }

  return cards;
})();

// =============================================================================
// RESOLUTION SELECTION
// =============================================================================

/** Whether the device has a high-DPI (retina) display */
export const isRetinaDisplay = typeof window !== 'undefined' && window.devicePixelRatio >= 2;

/** Get the resolution-appropriate card atlas path */
export function getCardAtlasPath(): string {
  return isRetinaDisplay ? ATLASES.cards.path2x : ATLASES.cards.path1x;
}

/** Get the resolution-appropriate card back image path */
export function getCardBackPath(): string {
  return isRetinaDisplay ? IMAGES.cardBack : IMAGES.cardBack1x;
}

// =============================================================================
// SOUND ASSETS
// Extracted from legacy AS3 clients - see LEGACY_DATA_USAGE.md
// =============================================================================

/** Sound file paths */
export const SOUNDS = {
  // ---------------------------------------------------------------------------
  // Card sounds (multiple variants for variety)
  // ---------------------------------------------------------------------------
  deal: [
    `${ASSET_PATHS.sounds}/deal1.mp3`,
    `${ASSET_PATHS.sounds}/deal2.mp3`,
    `${ASSET_PATHS.sounds}/deal3.mp3`,
    `${ASSET_PATHS.sounds}/deal4.mp3`,
    `${ASSET_PATHS.sounds}/deal5.mp3`,
    `${ASSET_PATHS.sounds}/deal6.mp3`,
  ],
  fold: [
    `${ASSET_PATHS.sounds}/fold1.mp3`,
    `${ASSET_PATHS.sounds}/fold2.mp3`,
    `${ASSET_PATHS.sounds}/fold3.mp3`,
  ],
  flop: [
    `${ASSET_PATHS.sounds}/flop1.mp3`,
    `${ASSET_PATHS.sounds}/flop2.mp3`,
    `${ASSET_PATHS.sounds}/flop3.mp3`,
    `${ASSET_PATHS.sounds}/flop4.mp3`,
  ],
  turn: [
    `${ASSET_PATHS.sounds}/turn1.mp3`,
    `${ASSET_PATHS.sounds}/turn2.mp3`,
    `${ASSET_PATHS.sounds}/turn3.mp3`,
    `${ASSET_PATHS.sounds}/turn4.mp3`,
    `${ASSET_PATHS.sounds}/turn5.mp3`,
    `${ASSET_PATHS.sounds}/turn6.mp3`,
  ],

  // ---------------------------------------------------------------------------
  // Chip sounds
  // ---------------------------------------------------------------------------
  chip: [
    `${ASSET_PATHS.sounds}/chip1.mp3`,
    `${ASSET_PATHS.sounds}/chip2.mp3`,
    `${ASSET_PATHS.sounds}/chip3.mp3`,
    `${ASSET_PATHS.sounds}/chip4.mp3`,
  ],
  chipsWin: `${ASSET_PATHS.sounds}/chips_win.mp3`,
  chipsEnd: `${ASSET_PATHS.sounds}/chips_end.mp3`,

  // ---------------------------------------------------------------------------
  // Action sounds
  // ---------------------------------------------------------------------------
  check: [
    `${ASSET_PATHS.sounds}/check1.mp3`,
    `${ASSET_PATHS.sounds}/check2.mp3`,
    `${ASSET_PATHS.sounds}/check3.mp3`,
  ],
  knock: [
    `${ASSET_PATHS.sounds}/knock1.mp3`,
    `${ASSET_PATHS.sounds}/knock2.mp3`,
    `${ASSET_PATHS.sounds}/knock3.mp3`,
  ],
  allin: [
    `${ASSET_PATHS.sounds}/allin1.mp3`,
    `${ASSET_PATHS.sounds}/allin2.mp3`,
    `${ASSET_PATHS.sounds}/allin3.mp3`,
  ],

  // ---------------------------------------------------------------------------
  // Alert sounds
  // ---------------------------------------------------------------------------
  act: `${ASSET_PATHS.sounds}/act.mp3`,
  second_act: `${ASSET_PATHS.sounds}/second_act.mp3`,
  tick: `${ASSET_PATHS.sounds}/tick.mp3`,

  // ---------------------------------------------------------------------------
  // UI sounds
  // ---------------------------------------------------------------------------
  buttonClick: `${ASSET_PATHS.sounds}/button_click.mp3`,
  pop: `${ASSET_PATHS.sounds}/pop.mp3`,
  popupOpen: `${ASSET_PATHS.sounds}/popup_open.mp3`,
  popupClose: `${ASSET_PATHS.sounds}/popup_close.mp3`,

  // ---------------------------------------------------------------------------
  // Rewards & celebrations
  // ---------------------------------------------------------------------------
  bonus: `${ASSET_PATHS.sounds}/bonus.mp3`,
  applause: `${ASSET_PATHS.sounds}/applause.mp3`,

  // ---------------------------------------------------------------------------
  // Ambient & music
  // ---------------------------------------------------------------------------
  roomNoise: `${ASSET_PATHS.sounds}/room_noise.mp3`,
  musicGroove: `${ASSET_PATHS.sounds}/music_groove.mp3`,
};

// =============================================================================
// FONTS
// =============================================================================

/** Font configurations */
export const FONTS = {
  /** Primary UI font */
  primary: {
    family: 'Roboto',
    weights: [400, 500, 700],
    path: `${ASSET_PATHS.fonts}/roboto`,
  },

  /** Numeric font (for chip counts) */
  numeric: {
    family: 'Roboto Mono',
    weights: [400, 700],
    path: `${ASSET_PATHS.fonts}/roboto-mono`,
  },

  /** Display font (for headers) */
  display: {
    family: 'Oswald',
    weights: [400, 700],
    path: `${ASSET_PATHS.fonts}/oswald`,
  },
};

// =============================================================================
// LOADING GROUPS
// Assets grouped by when they need to be loaded
// =============================================================================

/** Asset loading groups */
export const ASSET_GROUPS = {
  /** Critical assets for loading screen */
  boot: {
    name: 'boot',
    assets: [
      IMAGES.logo,
    ],
  },

  /** Core assets needed before lobby */
  core: {
    name: 'core',
    assets: [
      ATLASES.ui.path,
    ],
  },

  /** Table assets (can be loaded when entering table) */
  table: {
    name: 'table',
    assets: [
      // Note: cards atlas + card back are added dynamically by AssetService
      // based on devicePixelRatio (1x for standard, 2x for retina)
      ATLASES.chips.path,
      ATLASES.table.path,
      IMAGES.tableBackground,
      IMAGES.tableFelt,
      IMAGES.dealerButton,
    ],
  },

  /** Sound assets (can be loaded in background) */
  sounds: {
    name: 'sounds',
    assets: [
      ...SOUNDS.deal,
      ...SOUNDS.chip,
      SOUNDS.act,
      SOUNDS.chipsWin,
    ],
  },
};
